import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { Suspense } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { CiteThis } from "@/components/site/CiteThis";
import { Markdown } from "@/components/site/Markdown";
import { ShareBar } from "@/components/site/ShareBar";
import {
  getAlgorithmUpdate,
  listArticlesInRange,
  listChangelogEntries,
  listVolatilitySnapshots,
  type AlgorithmUpdate,
  type ChangelogEntry,
  type RelatedArticle,
  type VolatilitySnapshot,
} from "@/lib/backlink.functions";
import { SITE, formatDate } from "@/lib/site";

const updateQuery = (slug: string) =>
  queryOptions({
    queryKey: ["algorithm-update", slug],
    queryFn: () => getAlgorithmUpdate({ data: { slug } }),
  });

const relatedArticlesQuery = (from: string, to: string) =>
  queryOptions({
    queryKey: ["update-related-articles", from, to],
    queryFn: () => listArticlesInRange({ data: { from, to, limit: 6 } }),
  });

const changelogQuery = queryOptions({
  queryKey: ["changelog-entries"],
  queryFn: () => listChangelogEntries(),
});

const volatilityQuery = queryOptions({
  queryKey: ["volatility-snapshots"],
  queryFn: () => listVolatilitySnapshots(),
});

const SURFACE_WATCH: Record<string, string> = {
  "local-pack": "Rank tracking on the 3-pack for service-area and near-me queries.",
  gbp: "Category, attribute, and description changes on your Business Profile.",
  maps: "Google Maps discovery, driving-directions volume, and place-card layout.",
  ai: "AI Overviews and Ask Maps citations for local intent queries.",
  reviews: "Review velocity, filtering, and reply latency signals.",
  spam: "Suspensions, hard/soft edits, and listing quality flags.",
  ranking: "Overall organic ranking shifts blended with local intent.",
  "search-console": "Impressions and click delivery in Search Console.",
};

export const Route = createFileRoute("/updates/$slug")({
  loader: async ({ params, context }) => {
    const u = await context.queryClient.ensureQueryData(updateQuery(params.slug));
    if (!u) throw notFound();
    const { from, to } = relatedWindow(u);
    await Promise.all([
      context.queryClient.ensureQueryData(relatedArticlesQuery(from, to)),
      context.queryClient.ensureQueryData(changelogQuery),
      context.queryClient.ensureQueryData(volatilityQuery),
    ]);
    return u;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const u = loaderData as AlgorithmUpdate;
    const url = `${SITE.url}/updates/${params.slug}`;
    const title = `${u.name} — Local search algorithm update`;
    const desc = u.summary.slice(0, 158);
    return {
      meta: [
        { title: `${title} | ${SITE.name}` },
        { name: "description", content: desc },
        { property: "og:title", content: u.name },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "article:published_time", content: u.started_at },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: u.name,
            description: desc,
            datePublished: u.started_at,
            dateModified: u.updated_at,
            url,
            author: { "@type": "Organization", name: `${SITE.name} Newsroom`, url: SITE.url },
            publisher: { "@type": "Organization", name: SITE.name, url: SITE.url },
            keywords: [...(u.affected_surfaces as string[]), "local search", "algorithm update"].join(", "),
          }),
        },
      ],
    };
  },
  component: UpdatePage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-5 sm:px-8 py-24 text-center">
        <p className="eyebrow">Not found</p>
        <h1 className="headline-display mt-3 text-4xl">Update not found</h1>
        <Link to="/updates" className="mt-6 inline-block underline">Back to tracker</Link>
      </main>
      <SiteFooter />
    </div>
  ),
});

function UpdatePage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <Suspense fallback={<div className="py-24 text-center text-muted-foreground">Loading…</div>}>
          <UpdateBody />
        </Suspense>
      </main>
      <SiteFooter />
    </div>
  );
}

function UpdateBody() {
  const params = Route.useParams();
  const { data } = useSuspenseQuery(updateQuery(params.slug));
  const u = data as AlgorithmUpdate;
  const { from, to } = relatedWindow(u);
  const { data: related } = useSuspenseQuery(relatedArticlesQuery(from, to));
  const { data: allChangelog } = useSuspenseQuery(changelogQuery);
  const { data: allVol } = useSuspenseQuery(volatilityQuery);
  const url = `${SITE.url}/updates/${params.slug}`;

  const relatedArticles = related as RelatedArticle[];
  const fromMs = new Date(from).getTime();
  const toMs = new Date(to).getTime();
  const relatedChangelog = (allChangelog as ChangelogEntry[])
    .filter((c) => {
      const t = new Date(c.occurred_at).getTime();
      return t >= fromMs && t <= toMs;
    })
    .slice(0, 6);
  const nearVol = nearestSnapshots(allVol as VolatilitySnapshot[], u.started_at, 5);
  const surfaces = (u.affected_surfaces as string[]) ?? [];

  return (
    <article className="mx-auto max-w-3xl px-5 sm:px-8 py-16">
      <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
        <ol className="flex items-center gap-2 flex-wrap">
          <li><Link to="/" className="hover:text-accent">Home</Link></li>
          <li aria-hidden>/</li>
          <li><Link to="/updates" className="hover:text-accent">Updates</Link></li>
        </ol>
      </nav>
      <header className="mt-6">
        <p className="eyebrow">Algorithm update · {u.status}</p>
        <h1 className="headline-display mt-3 text-4xl sm:text-5xl">{u.name}</h1>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs uppercase tracking-wider text-muted-foreground">
          <span>Started {formatDate(u.started_at)}</span>
          {u.ended_at && <span>Ended {formatDate(u.ended_at)}</span>}
          <span>Severity {Number(u.severity).toFixed(1)} / 10</span>
        </div>
      </header>

      <p className="mt-8 text-xl leading-relaxed font-serif text-foreground/90">{u.summary}</p>

      {u.body_md ? (
        <div className="mt-8"><Markdown content={u.body_md} /></div>
      ) : surfaces.length > 0 ? (
        <section className="mt-10 rounded-sm border border-border bg-paper-soft p-6">
          <p className="eyebrow">What we're watching</p>
          <ul className="mt-3 space-y-2 text-base text-foreground/85">
            {surfaces.map((s) => (
              <li key={s} className="flex gap-3">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-accent shrink-0" aria-hidden />
                <span><strong className="font-semibold">{s}:</strong> {SURFACE_WATCH[s] ?? "Signals tracked across the Map Observer volatility index."}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="mt-8 flex flex-wrap items-center gap-2 text-xs">
        <span className="eyebrow mr-2">Affected surfaces</span>
        {surfaces.map((s) => (
          <span key={s} className="rounded-sm border border-border bg-paper-soft px-2 py-0.5">{s}</span>
        ))}
      </div>

      {u.source_url && (
        <p className="mt-6 text-sm">
          <span className="eyebrow mr-2">Source</span>
          <a href={u.source_url} target="_blank" rel="noopener noreferrer" className="underline text-ochre-deep hover:text-accent">
            {u.source_url}
          </a>
        </p>
      )}

      {nearVol.length > 0 && (
        <section className="mt-12">
          <div className="flex items-baseline justify-between border-b border-foreground pb-3 mb-4">
            <h2 className="headline-display text-2xl">Volatility around this window</h2>
            <Link to="/local-search-index" className="text-xs uppercase tracking-wider text-muted-foreground hover:text-accent">
              Live index →
            </Link>
          </div>
          <ul className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {nearVol.map((v) => (
              <li key={v.id} className="rounded-sm border border-border bg-paper-soft px-3 py-2">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{formatDate(v.snapshot_date)}</p>
                <p className="text-xl font-bold leading-none mt-1">{Number(v.score).toFixed(1)}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {relatedArticles.length > 0 && (
        <section className="mt-12">
          <div className="border-b border-foreground pb-3 mb-4">
            <h2 className="headline-display text-2xl">Related coverage</h2>
          </div>
          <ul className="space-y-4">
            {relatedArticles.map((a) => (
              <li key={a.slug}>
                <Link to="/article/$slug" params={{ slug: a.slug }} className="group block">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{a.published_at ? formatDate(a.published_at) : ""}</p>
                  <p className="mt-1 font-semibold group-hover:text-accent">{a.title}</p>
                  {a.excerpt && <p className="mt-1 text-sm text-foreground/70 line-clamp-2">{a.excerpt}</p>}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {relatedChangelog.length > 0 && (
        <section className="mt-12">
          <div className="border-b border-foreground pb-3 mb-4">
            <h2 className="headline-display text-2xl">Changelog in this window</h2>
          </div>
          <ul className="space-y-3">
            {relatedChangelog.map((c) => (
              <li key={c.id}>
                <Link to="/changelog/$slug" params={{ slug: c.slug }} className="group block">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{formatDate(c.occurred_at)} · {c.surface}</p>
                  <p className="mt-1 font-semibold group-hover:text-accent">{c.title}</p>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-b border-border py-5">
        <CiteThis title={u.name} url={url} publishedDate={u.started_at} />
        <ShareBar url={url} title={u.name} text={u.summary} />
      </div>

      <NewsletterCTA />
    </article>
  );
}

function relatedWindow(u: AlgorithmUpdate): { from: string; to: string } {
  const start = new Date(u.started_at);
  const end = u.ended_at ? new Date(u.ended_at) : new Date();
  const from = new Date(start.getTime() - 3 * 24 * 60 * 60 * 1000);
  const to = new Date(end.getTime() + 14 * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

function nearestSnapshots(all: VolatilitySnapshot[], anchorISO: string, n: number): VolatilitySnapshot[] {
  const anchor = new Date(anchorISO).getTime();
  return [...all]
    .sort((a, b) => Math.abs(new Date(a.snapshot_date).getTime() - anchor) - Math.abs(new Date(b.snapshot_date).getTime() - anchor))
    .slice(0, n)
    .sort((a, b) => new Date(a.snapshot_date).getTime() - new Date(b.snapshot_date).getTime());
}

